(ns admin.components.learner-reports.question-body
  (:require [clojure.string :as str]
            [admin.components.learner-reports.shared :as lrs]
            [admin.components.learner-reports.feedback :as feedback]
            [kyber.components.base.text :as text]
            [kyber.styles.core :refer [gs styles->classes]]
            [kyber.components.base.icons :as icon]
            [kyber.utils :as ku :refer [idx->letters]]
            [potpuri.core :as pot]))

(def classes
  (styles->classes
   {:question-outer-container
    {:padding (gs [:spacing :p12])
     :border-radius (gs [:radius :r3])
     :border (str "1px solid " (gs [:colors :gray-scale-100]))
     :background-color (gs [:colors :gray-scale-50])}

    :question-title
    {:padding [(gs [:spacing :p0])
               (gs [:spacing :p24])]
     :margin-top (gs [:spacing :p20])}

    :results-container
    {:display :flex
     :margin-left (gs [:spacing :-p24])
     :margin-right (gs [:spacing :-p24])
     :padding [(gs [:spacing :p0])
               (gs [:spacing :p24])
               (gs [:spacing :p24])]}

    :split-container
    {:padding [(gs [:spacing :p0])
               (gs [:spacing :p24])]}

    :full-width
    {:width "100%"}

    :one-third-width
    {:width "33.333%"}

    :two-thirds-width
    {:width "66.666%"}

    :answer-choice
    {:display :flex
     :align-items :baseline
     :margin-bottom (gs [:spacing :p8])
     "& > *:first-child"
     {:width (gs [:spacing :p20])}
     "& > *:last-child"
     {}}

    :result-bar-container
    {:display :flex
     :align-items :center}

    :result-bar-background
    {:height (gs [:spacing :p4])
     :background-color (gs [:colors :gray-scale-200])
     :width "100%"
     :position :relative}

    :result-bar
    {:background-color (gs [:colors :primary-lite-600])
     :height (gs [:spacing :p4])
     :position :absolute}

    :flex-baseline
    {:display :flex
     :align-items :baseline}

    :pass-fail-icon
    {:width (gs [:spacing :p16])
     :text-align :center}

    :post-test-attempt
    {"& span.attempt"
     {:width (gs [:spacing :p32])}}

    :rating-scale-body
    {:border (str "1px solid " (gs [:colors :gray-scale-200]))
     :margin [(gs [:spacing :p20])
              (gs [:spacing :p0])]
     :padding [(gs [:spacing :p16])
               (gs [:spacing :p0])
               (gs [:spacing :p20])]
     :display :flex}}))

(defn- choice-chosen-count
  "Adds all the possible confidence choice counts"
  [{:question.aggregates.response/keys [guess-count
                                        no-confidence-count
                                        sure-count
                                        unsure-count]}]
  (+ 0 guess-count no-confidence-count sure-count unsure-count))

(defn format-letters [choices]
  (cond
    (vector? choices)
    (->> choices
         (map :question.choice/index)
         sort
         (map idx->letters)
         (str/join ", "))

    (map? choices)
    (-> choices :question.choice/index idx->letters)

    :else
    "skipped"))

(defn pass-fail-icon [answered-correctly]
  (cond
    (true? answered-correctly)
    [icon/icon {:class (:pass-fail-icon classes)
                :style {:color (gs [:colors :success-500])}} :check-regular]

    (false? answered-correctly)
    [icon/icon {:class (:pass-fail-icon classes)
                :style {:color (gs [:colors :error-700])}} :times]

    :else
    [:<>]))

(defn- percentage-bar [{:keys [percent-chosen hide-label]
                        :or {hide-label false}}]
  [:div {:class (:result-bar-container classes)}
   [:div {:class (:result-bar-background classes)}
    [:div {:style {:width (str percent-chosen "%")}
           :class (:result-bar classes)}]]
   (when (false? hide-label)
     [text/title-10 :span
      {:style {:width (gs [:spacing :p36])
               :margin-left (gs [:spacing :p12])}}
      (str (lrs/single-decimal percent-chosen) "%")])])

(defn- choices-legend [message]
  [:div {:class (:flex-baseline classes)
         :style {:margin-top (gs [:spacing :p24])}}
   [:div {:style {:width (gs [:spacing :p24])
                  :margin-right (gs [:spacing :p12])}}
    [percentage-bar {:percent-chosen 50
                     :hide-label true}]]
   [text/body-20 :span message]])

(defn- confidence->label [confidence]
  (case confidence
    :sure "I'm Sure"
    :unsure "I Think"
    :guess "I'm Guessing"
    :no-confidence ""))

(defn- learner-free-response [free-response-text]
  (when (some? free-response-text)
    [text/body-20 :span {:style {:margin-left (gs [:spacing :p8])}}
     (str " \" " free-response-text " \" ")]))

(defn- test-answer
  [{question-type :question/type
    :question.response/keys [correct? confidence value]}]
  [:p {:class (:flex-baseline classes)}
   [:span {:style {:margin-right (gs [:spacing :p8])}}
    [pass-fail-icon correct?]]

   (when (not= question-type "free-response")
     [text/title-20 :span {} (format-letters value)])

   (when (and (some? confidence)
              (not= confidence :no-confidence))
     [text/body-20 :span {:style {:margin-left (gs [:spacing :p8])}}
      (str "(" (confidence->label confidence) ")")])

   (when (= question-type "free-response")
     (learner-free-response value))])

(defn pre-test-wrapper [{:keys [pre-test-component]}]
  [:div {:style {:margin-bottom (gs [:spacing :p36])}}
   [text/title-20 :p "Pre-test Answer:"]
   pre-test-component])

(defn pre-test-answers [{:question/keys [pre-test-attempt] :as question}]
  (if (some? pre-test-attempt)
    [pre-test-wrapper
     {:pre-test-component
      [test-answer (merge (select-keys question [:question/type])
                          pre-test-attempt)]}]
    [:div {:style {:margin-bottom (gs [:spacing :p36])}}
     [text/title-20 :p "Pre-test:"]
     [text/caption-20 :p "This question was not presented on the pre-test"]]))

(defn post-test-wrapper
  [{:keys [total-attempts
           last-attempt-component
           first-attempt-component]}]
  [:div
   [text/title-20 :p "Post-test Answer:"]
   [:div {:class [(:post-test-attempt classes)
                  (:flex-baseline classes)]}
    [text/body-20 :span.attempt {} (ku/get-ordinal 1)]
    first-attempt-component]
   (when (> total-attempts 2)
     [text/body-10 :span "..."])
   (when (some? last-attempt-component)
     [:div {:class [(:post-test-attempt classes)
                    (:flex-baseline classes)]}
      [text/body-20 :span.attempt {} (ku/get-ordinal total-attempts)]
      last-attempt-component])])

(defn post-test-answers [{:question/keys [first-attempt last-attempt total-attempts] :as question}]
  [post-test-wrapper
   (cond-> {:total-attempts total-attempts
            :first-attempt-component
            [test-answer (merge (select-keys question [:question/type])
                                first-attempt)]}
     (> total-attempts 1)
     (assoc :last-attempt-component
            [test-answer (merge (select-keys question [:question/type])
                                last-attempt)]))])

(defn moment-question-answer [question]
  [:div {:style {:margin-bottom (gs [:spacing :p36])}}
   [text/title-20 :p "Learner Answer:"]
   [test-answer question]])

(defn render-learner-answers
  [question is-moment]
  [:<>
   (if is-moment
     [moment-question-answer question]
     [:<>
      [pre-test-answers question]
      [post-test-answers question]])])

(defn single-answer-choices [{:question/keys [choices correct-answer]
                              :question.aggregates/keys [responses]} is-moment]
  (let [choice-results-lookup
        (pot/index-by #(get-in % [:question.aggregates.response/choice :question.choice/uuid]) responses)

        total-participants
        (reduce #(+ %1 (choice-chosen-count %2)) 0 responses)]
    [:div {:class [(:split-container classes)
                   (:two-thirds-width classes)]}
     [text/title-20 :p
      (when (some? correct-answer)
        (str "Answer Choices: The correct answer is: " (-> correct-answer :question.choice/index idx->letters)))]
     [:ul
      (for [{choice-uuid :question.choice/uuid
             :question.choice/keys [body index]} choices]
        (let [percent-chosen (* (/ (choice-chosen-count (get choice-results-lookup choice-uuid))
                                   total-participants)
                                100)]
          [:li {:key choice-uuid :class (:answer-choice classes)}
           [text/title-10 :span {} (idx->letters index)]
           [:div {:style {:flex-grow 1}}
            [text/title-10 :span body]
            [percentage-bar {:percent-chosen (if (js/isNaN percent-chosen) 0 percent-chosen)}]]]))]
     [choices-legend (str "Percentage of learners that chose this answer"
                          (when-not is-moment
                            " on their first Post-test"))]]))

(defn- render-single-answer-body
  [question is-moment]
  [:div {:class (:results-container classes)}
   [single-answer-choices question is-moment]
   [:div {:class [(:split-container classes)
                  (:one-third-width classes)]}
    [render-learner-answers question is-moment]]])

(defn- multiple-answer-choices
  [{:question/keys [has-correctness correct-answer choices]
    :question.aggregates/keys [percent-correct]} is-moment]
  [:div {:class [(:split-container classes)
                 (:two-thirds-width classes)]}
   [text/title-20 :p
    (cond-> "Answer Choices:"
      (true? has-correctness)
      (str " The correct answers are: "
           (format-letters correct-answer)))]

   [:ul
    (for [{choice-uuid :question.choice/uuid
           :question.choice/keys [index body]} choices]
      [:li {:key choice-uuid :class (:answer-choice classes)}
       [text/title-10 :p {} (idx->letters index)]
       [text/title-10 :p body]])]

   (when (true? has-correctness)
     [:div {:style {:margin-top (gs [:spacing :p20])}}
      [:div {:style {:display :flex
                     :justify-content :space-between}}
       [text/title-10 :p {} (str (ku/->percent percent-correct) "% Correct")]
       [text/title-10 :p {} (str (ku/->percent (- 1 percent-correct)) "% Incorrect")]]
      [percentage-bar {:percent-chosen (* percent-correct 100)
                       :hide-label true}]
      [choices-legend (str "Percentage of learners that got this answer correct /
                       incorrect"
                           (when-not is-moment
                             " on their first Post-test"))]])])

(defn- multiple-choice-body [question is-moment]
  [:div {:class (:results-container classes)}
   [multiple-answer-choices question]
   [:div {:class [(:split-container classes)
                  (:one-third-width classes)]}
    [render-learner-answers question is-moment]]])

(defn- free-response-body [question is-moment]
  [:div {:class (:results-container classes)}
   [:div {:class [(:split-container classes)
                  (:full-width classes)]}
    [render-learner-answers question is-moment]]])

(defn rating-scale-body
  [{:question/keys
    [choices headers total-attempts other-enabled first-attempt last-attempt]}]
  (let [headers-lookup
        (pot/index-by :question.header/index headers)

        attempt-lookup
        #(->> %
              :question.response/value
              (pot/index-by :question.choice/index))

        rating-scale-answer
        (fn [choice attempt]
          (or (->> choice
                   :question.choice/index
                   (get (attempt-lookup attempt))
                   :question.header/index
                   (get headers-lookup)
                   :question.header/body)
          "N/A"))]
    [:div {:class (:results-container classes)}
     [:div {:class [(:split-container classes)
                    (:full-width classes)]}
      [:ul
       (for [choice choices]
         ^{:key choice}
         [:li {:class (:rating-scale-body classes)}
          [:div {:class [(:split-container classes)
                         (:two-thirds-width classes)]}
           [text/title-20 :p (:question.choice/body choice)]
           (for [header (cond-> headers
                          (true? other-enabled)
                          (conj {:question.header/body "N/A"}))]
             ^{:key header}
             [:div
              [text/title-10 :p (:question.header/body header)]
              #_[percentage-bar {:percent-chosen 0}]])]
          (when (pos? total-attempts)
            [:div {:class [(:split-container classes)
                           (:one-third-width classes)]}
             [post-test-wrapper
              (cond-> {:total-attempts total-attempts
                       :first-attempt-component
                       [text/body-20 :span {}
                        (rating-scale-answer choice first-attempt)]}
                (> total-attempts 1)
                (assoc :last-attempt-component
                       [text/body-20 :span {}
                        (rating-scale-answer choice last-attempt)]))]])])]]]))

(defn render [{:keys [question resources is-moment user-uuid]
               :or {is-moment false}}]
  (let [{question-type :question/type
         :question/keys [educator-feedback body]} question]
    [:<>
    ;; user-uuid added to data-test attritube to prevent learner content from matching other learner's content
    ;; to ensure re-rendering when learner is changed. 
     [:div {:class [(:question-title classes)] :data-test (str "user-uuid-" user-uuid)}
      [text/title-20 :p (str "Question")]

      [:div {:dangerouslySetInnerHTML {:__html body}}]]

     (case question-type
       "single-answer"
       [render-single-answer-body question is-moment]

       "multiple-answer"
       [multiple-choice-body question is-moment]

       "free-response"
       [free-response-body question is-moment]

       "rating-scale"
       [rating-scale-body question]

       (js/console.error "Question type is not supported"))

     (when (or (seq resources)
               (some? educator-feedback))
       [feedback/render {:educator-feedback educator-feedback
                         :resources resources}])]))
